import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Syringe, AlertTriangle, CheckCircle2, Eye, Plus,
    Stethoscope, Pill, ChevronDown, Edit2, Trash2, Upload, Calendar
} from 'lucide-react';
import { api } from '../../services/api';
import { buildVaccineSchedule, groupVaccineHistory, type VaccineScheduleRow } from '../../data/vaccinePlans';
import { todayLocal } from '../../utils/formatters';
import { scheduleLocalReminder, scheduleDailyReminder, cancelLocalReminder } from '../../services/localReminders';

// --- UTILS ---
const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = String(dateStr).slice(0, 10).split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

interface BreederPetHealthProps {
    pet: any;
    type: 'vaccines' | 'consultations';
    onAddRecord: () => void;
    onEditRecord?: (consult: any) => void;
    onDeleteRecord?: (consult: any) => void;
    /** Solo lectura (ejemplar ya entregado a su nuevo dueño). */
    readOnly?: boolean;
}

// Mismo motor del Carnet Sanitario de la app de usuarios (data/vaccinePlans), con la
// misma interacción por fila (registrar/editar/borrar/adjuntar) y los mismos
// recordatorios locales — así el criadero recibe las mismas alertas que un dueño
// dentro de la APK, no solo un listado de solo lectura.
export const BreederPetHealth: React.FC<BreederPetHealthProps> = ({ pet, type, onAddRecord, onEditRecord, onDeleteRecord, readOnly }) => {
    const [loading, setLoading] = useState(true);
    const [vaccines, setVaccines] = useState<any[]>([]);
    const [consultations, setConsultations] = useState<any[]>([]);
    const [expandedConsultationId, setExpandedConsultationId] = useState<string | null>(null);

    const loadData = async () => {
        if (!pet) return;
        setLoading(true);
        try {
            if (type === 'vaccines') {
                const data = await api.getVaccines(pet.id);
                setVaccines(Array.isArray(data) ? data : []);
            } else {
                const data = await api.getHealthRecords(pet.id);
                setConsultations(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error("Error loading health data", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pet, type]);

    const birth = useMemo(() => {
        if (!pet?.birthDate) return null;
        const d = new Date(`${String(pet.birthDate).slice(0, 10)}T00:00:00`);
        return isNaN(d.getTime()) ? null : d;
    }, [pet]);

    const vaccineStatus = useMemo(() => groupVaccineHistory(vaccines), [vaccines]);
    const schedule = useMemo(
        () => (birth && type === 'vaccines' ? buildVaccineSchedule(birth, pet.species || 'Perro', vaccineStatus) : []),
        [birth, pet, vaccineStatus, type]
    );
    const pendingRows = useMemo(
        () => schedule.filter(r => !r.isDone).sort((a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime()),
        [schedule]
    );
    const doneRows = useMemo(
        () => schedule.filter(r => r.isDone).sort((a, b) => String(b.realDate || b.targetDate).localeCompare(String(a.realDate || a.targetDate))),
        [schedule]
    );
    const scheduleSections = [
        { key: 'pending', title: 'Pendientes', rows: pendingRows },
        { key: 'done', title: 'Realizadas', rows: doneRows },
    ].filter(s => s.rows.length > 0);

    // Recordatorios locales en el dispositivo del criador — mismo criterio que la
    // app de usuarios: amarilla una vez 7 días antes, roja insistiendo a diario
    // desde el día anterior hasta que se registre la aplicación.
    const notifIds = useRef<Set<number>>(new Set());
    useEffect(() => {
        if (readOnly || type !== 'vaccines') return;
        const hashId = (key: string) => {
            let hash = 0;
            for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
            return Math.abs(hash) % 1000000;
        };
        const pending = schedule.filter(r => !r.isDone && r.targetDate);
        const nextActiveIds = new Set<number>();
        pending.forEach(row => {
            if (row.alertType !== 'warning' && row.alertType !== 'danger') return;
            const id = hashId(`${pet?.id}-${row.uniqueKey}`);
            nextActiveIds.add(id);
            if (row.alertType === 'danger') {
                const start = new Date(row.targetDate);
                start.setDate(start.getDate() - 1);
                start.setHours(9, 0, 0, 0);
                scheduleDailyReminder(id, `⚠️ Vacuna vencida: ${row.name}`, `${pet?.name || 'Un ejemplar'} tiene pendiente "${row.name}". Regístrala apenas se la apliques.`, start);
            } else {
                const target = new Date(row.targetDate);
                target.setDate(target.getDate() - 7);
                target.setHours(9, 0, 0, 0);
                scheduleLocalReminder(id, `Vacuna en 7 días: ${row.name}`, `${pet?.name || 'Un ejemplar'} tiene "${row.name}" agendada para pronto.`, target);
            }
        });
        notifIds.current.forEach(id => { if (!nextActiveIds.has(id)) cancelLocalReminder(id); });
        notifIds.current = nextActiveIds;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedule, readOnly, type]);

    // Registrar una tarea pendiente puntual del carnet
    const [registering, setRegistering] = useState<VaccineScheduleRow | null>(null);
    const [regDate, setRegDate] = useState('');
    const [regPlace, setRegPlace] = useState('');
    const [savingReg, setSavingReg] = useState(false);

    const openRegister = (row: VaccineScheduleRow) => { setRegistering(row); setRegDate(todayLocal()); setRegPlace(''); };

    const confirmRegister = async () => {
        if (!registering || !regDate || savingReg) return;
        if ((vaccineStatus[registering.name] || []).some(r => String(r.date).slice(0, 10) === regDate)) { setRegistering(null); return; }
        setSavingReg(true);
        try {
            await api.addVaccine({ pet_id: pet.id, name: registering.name, date_applied: regDate, place: regPlace || 'Criadero' });
            setRegistering(null);
            loadData();
        } catch (e) {
            alert('No se pudo registrar. Intenta nuevamente.');
        } finally {
            setSavingReg(false);
        }
    };

    // Corregir o borrar una vacuna ya registrada
    const getRecordForRow = (row: VaccineScheduleRow) => (vaccineStatus[row.name] || [])[row.historyIndex ?? -1];
    const [editingVaccine, setEditingVaccine] = useState<{ row: VaccineScheduleRow; record: any } | null>(null);
    const [editDate, setEditDate] = useState('');
    const [editPlace, setEditPlace] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingVaccine, setDeletingVaccine] = useState<{ row: VaccineScheduleRow; record: any } | null>(null);
    const [deletingVaccineBusy, setDeletingVaccineBusy] = useState(false);

    const openEditVaccine = (row: VaccineScheduleRow) => {
        const record = getRecordForRow(row);
        if (!record?.id) return;
        setEditingVaccine({ row, record });
        setEditDate(String(record.date).slice(0, 10));
        setEditPlace(record.place || '');
    };

    const confirmEditVaccine = async () => {
        if (!editingVaccine || !editDate || savingEdit) return;
        setSavingEdit(true);
        try {
            await api.updateVaccine(String(editingVaccine.record.id), { date_applied: editDate, place: editPlace });
            setEditingVaccine(null);
            loadData();
        } catch (e) {
            alert('No se pudo guardar. Intenta nuevamente.');
        } finally {
            setSavingEdit(false);
        }
    };

    const confirmDeleteVaccine = async () => {
        if (!deletingVaccine?.record?.id || deletingVaccineBusy) return;
        setDeletingVaccineBusy(true);
        try {
            await api.deleteVaccine(String(deletingVaccine.record.id));
            loadData();
        } finally {
            setDeletingVaccineBusy(false);
            setDeletingVaccine(null);
        }
    };

    // Adjuntar comprobante a una tarea ya realizada
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadingRow, setUploadingRow] = useState<VaccineScheduleRow | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);

    const triggerUpload = (row: VaccineScheduleRow) => { setUploadingRow(row); fileInputRef.current?.click(); };

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !uploadingRow) return;
        const history = vaccineStatus[uploadingRow.name] || [];
        const record = history[uploadingRow.historyIndex ?? -1];
        if (!record?.id) { setUploadingRow(null); return; }
        setUploadingFile(true);
        try {
            const res = await api.uploadImage(file);
            if (res?.url) {
                await api.updateVaccine(String(record.id), { attachment_url: res.url, attachment_name: file.name });
                loadData();
            }
        } finally {
            setUploadingFile(false);
            setUploadingRow(null);
        }
    };

    if (loading) return <div className="p-10 text-center text-slate-400">Cargando...</div>;

    if (type === 'vaccines') {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white rounded-[7px] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="bg-sky-500 p-6 text-white flex justify-between items-center gap-3">
                        <div>
                            <h3 className="text-xl font-black uppercase italic tracking-tighter flex items-center gap-2"><Syringe size={20} /> Carnet Sanitario</h3>
                            <p className="text-[10px] font-bold text-sky-100 uppercase tracking-widest">Plan de vacunación y desparasitación</p>
                        </div>
                        {!readOnly && (
                            <button onClick={onAddRecord} className="bg-white text-sky-600 px-4 py-2 rounded-[7px] text-[10px] font-black uppercase shadow-lg hover:scale-105 transition-transform flex items-center gap-2 shrink-0">
                                <Plus size={14} /> Otra vacuna
                            </button>
                        )}
                    </div>

                    {!birth ? (
                        <div className="p-8 text-center">
                            <Calendar size={28} className="mx-auto text-slate-300 mb-2" />
                            <p className="text-sm font-bold text-slate-400">Agrega la fecha de nacimiento de {pet.name} para generar su calendario preventivo.</p>
                        </div>
                    ) : schedule.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-sm font-bold text-slate-400">Sin plan preventivo definido para esta especie todavía.</p>
                        </div>
                    ) : (
                        scheduleSections.map(section => (
                            <div key={section.key}>
                                <p className="px-5 pt-4 pb-2 text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50/60">{section.title} · {section.rows.length}</p>
                                <div className="divide-y divide-slate-50">
                                    {section.rows.map(row => {
                                        const isOverdue = !row.isDone && row.alertType === 'danger';
                                        const isWarn = !row.isDone && row.alertType === 'warning';
                                        return (
                                            <div key={row.uniqueKey} className={`p-4 flex items-center gap-3 ${isOverdue ? 'bg-red-50' : isWarn ? 'bg-amber-50' : ''}`}>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`font-black text-sm uppercase tracking-tight truncate ${isOverdue ? 'text-red-900' : isWarn ? 'text-amber-900' : 'text-slate-800'}`}>{row.name}</p>
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-[7px] uppercase tracking-widest ${row.isDone ? 'text-emerald-600 bg-emerald-50' : isOverdue ? 'text-red-700 bg-red-100' : isWarn ? 'text-amber-700 bg-amber-100' : 'text-sky-600 bg-sky-50'}`}>{row.isDone ? formatDate(row.realDate || row.targetDate) : row.ageLabel}</span>
                                                        {!row.isDone && row.alertMsg && (
                                                            <span className={`text-[9px] font-bold uppercase flex items-center gap-1 ${row.alertType === 'danger' ? 'text-red-500' : row.alertType === 'warning' ? 'text-amber-500' : 'text-slate-400'}`}>
                                                                <AlertTriangle size={10} /> {row.alertMsg}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {row.isDone ? (
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <span className="bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full text-[9px] font-black uppercase flex items-center gap-1 mr-1"><CheckCircle2 size={11} /> Listo</span>
                                                        {!readOnly && (
                                                            <>
                                                                <button onClick={() => openEditVaccine(row)} className="w-8 h-8 bg-slate-100 text-slate-500 rounded-[7px] flex items-center justify-center" title="Editar"><Edit2 size={13} /></button>
                                                                <button onClick={() => setDeletingVaccine({ row, record: getRecordForRow(row) })} className="w-8 h-8 bg-rose-50 text-rose-500 rounded-[7px] flex items-center justify-center" title="Eliminar"><Trash2 size={13} /></button>
                                                            </>
                                                        )}
                                                        {row.attachment ? (
                                                            <button onClick={() => window.open(row.attachment!, '_blank')} className="w-8 h-8 bg-sky-500 text-white rounded-[7px] flex items-center justify-center" title="Ver archivo"><Eye size={13} /></button>
                                                        ) : !readOnly && (
                                                            <button onClick={() => triggerUpload(row)} disabled={uploadingFile} className="w-8 h-8 bg-emerald-500 text-white rounded-[7px] flex items-center justify-center disabled:opacity-50" title="Subir archivo"><Upload size={13} /></button>
                                                        )}
                                                    </div>
                                                ) : !readOnly && (
                                                    <button onClick={() => openRegister(row)}
                                                        className={`w-9 h-9 text-white rounded-[7px] shadow-md flex items-center justify-center shrink-0 active:scale-95 transition-all ${row.alertType === 'danger' ? 'bg-red-500 ring-2 ring-red-400 animate-pulse' : row.alertType === 'warning' ? 'bg-amber-500 ring-2 ring-amber-300' : 'bg-sky-500'}`}
                                                        title="Registrar aplicación">
                                                        <Plus size={17} strokeWidth={3} />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <input type="file" accept="image/*,application/pdf" ref={fileInputRef} className="hidden" onChange={onFileChange} />

                {/* Registrar aplicación de una tarea pendiente */}
                {registering && (
                    <div className="fixed inset-0 z-[600] bg-black/50 flex items-center justify-center p-4" onClick={() => !savingReg && setRegistering(null)}>
                        <div className="bg-white rounded-[7px] w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
                            <h4 className="text-base font-black text-slate-800 uppercase mb-1">{registering.name}</h4>
                            <p className="text-xs text-slate-400 font-bold mb-4">Registrar aplicación</p>

                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wide block mb-1.5">Fecha<span className="text-orange-500 ml-1">*</span></label>
                            <input type="date" value={regDate} max={todayLocal()} onChange={e => setRegDate(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-[7px] text-sm font-bold text-slate-700 outline-none focus:border-sky-400 transition-all" />

                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wide block mb-1.5 mt-4">Lugar <span className="text-slate-400 normal-case font-bold">(opcional)</span></label>
                            <input type="text" value={regPlace} placeholder="Ej: Criadero" onChange={e => setRegPlace(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-[7px] text-sm font-bold text-slate-700 outline-none focus:border-sky-400 transition-all" />

                            <div className="flex gap-2 mt-6">
                                <button onClick={() => setRegistering(null)} disabled={savingReg} className="flex-1 py-3.5 bg-slate-100 text-slate-600 rounded-[7px] font-black text-xs uppercase disabled:opacity-50">Cancelar</button>
                                <button onClick={confirmRegister} disabled={savingReg || !regDate} className="flex-1 py-3.5 bg-sky-500 text-white rounded-[7px] font-black text-xs uppercase disabled:opacity-50">{savingReg ? 'Guardando…' : 'Guardar'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Editar una tarea ya realizada */}
                {editingVaccine && (
                    <div className="fixed inset-0 z-[600] bg-black/50 flex items-center justify-center p-4" onClick={() => !savingEdit && setEditingVaccine(null)}>
                        <div className="bg-white rounded-[7px] w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
                            <h4 className="text-base font-black text-slate-800 uppercase mb-1">{editingVaccine.row.name}</h4>
                            <p className="text-xs text-slate-400 font-bold mb-4">Editar aplicación</p>

                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wide block mb-1.5">Fecha<span className="text-orange-500 ml-1">*</span></label>
                            <input type="date" value={editDate} max={todayLocal()} onChange={e => setEditDate(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-[7px] text-sm font-bold text-slate-700 outline-none focus:border-sky-400 transition-all" />

                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wide block mb-1.5 mt-4">Lugar <span className="text-slate-400 normal-case font-bold">(opcional)</span></label>
                            <input type="text" value={editPlace} placeholder="Ej: Criadero" onChange={e => setEditPlace(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-[7px] text-sm font-bold text-slate-700 outline-none focus:border-sky-400 transition-all" />

                            <div className="flex gap-2 mt-6">
                                <button onClick={() => setEditingVaccine(null)} disabled={savingEdit} className="flex-1 py-3.5 bg-slate-100 text-slate-600 rounded-[7px] font-black text-xs uppercase disabled:opacity-50">Cancelar</button>
                                <button onClick={confirmEditVaccine} disabled={savingEdit || !editDate} className="flex-1 py-3.5 bg-sky-500 text-white rounded-[7px] font-black text-xs uppercase disabled:opacity-50">{savingEdit ? 'Guardando…' : 'Guardar'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirmación de borrado */}
                {deletingVaccine && (
                    <div className="fixed inset-0 z-[600] bg-black/50 flex items-center justify-center p-4" onClick={() => !deletingVaccineBusy && setDeletingVaccine(null)}>
                        <div className="bg-white rounded-[7px] w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
                            <div className="w-14 h-14 bg-rose-50 text-rose-500 rounded-[7px] flex items-center justify-center mx-auto mb-4"><Trash2 size={24} /></div>
                            <h4 className="text-base font-black text-slate-800 mb-1.5">¿Eliminar "{deletingVaccine.row.name}"?</h4>
                            <p className="text-sm text-slate-400 font-medium mb-6">Esta acción no puede deshacerse.</p>
                            <div className="flex gap-2">
                                <button onClick={() => setDeletingVaccine(null)} disabled={deletingVaccineBusy} className="flex-1 py-3.5 bg-slate-100 text-slate-600 rounded-[7px] font-black text-xs uppercase disabled:opacity-50">Cancelar</button>
                                <button onClick={confirmDeleteVaccine} disabled={deletingVaccineBusy} className="flex-1 py-3.5 bg-rose-500 text-white rounded-[7px] font-black text-xs uppercase disabled:opacity-50">{deletingVaccineBusy ? 'Eliminando…' : 'Eliminar'}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (type === 'consultations') {
        return (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-violet-50 p-6 rounded-[7px] border border-violet-100 flex justify-between items-center gap-3 mb-6 shadow-sm">
                    <div>
                        <h3 className="text-xl font-black text-violet-800 uppercase italic tracking-tighter flex items-center gap-2"><Stethoscope size={20} /> Historial Médico</h3>
                        <p className="text-[10px] font-bold text-violet-400 uppercase tracking-widest">Consultas y Tratamientos</p>
                    </div>
                    {!readOnly && (
                        <button onClick={onAddRecord} className="bg-violet-600 text-white px-4 py-2 rounded-[7px] text-[10px] font-black uppercase shadow-lg hover:bg-violet-700 transition-colors flex items-center gap-2 shrink-0">
                            <Plus size={14} /> Nueva Entrada
                        </button>
                    )}
                </div>

                {consultations.length === 0 ? (
                    <div className="text-center py-10 bg-slate-50 rounded-[7px] border-2 border-dashed border-slate-200">
                        <Stethoscope size={40} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">No hay historial médico registrado</p>
                    </div>
                ) : (
                    consultations.map((consult) => (
                        <div key={consult.id} className="bg-white rounded-[7px] border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-6 flex items-center justify-between gap-3">
                                <div onClick={() => setExpandedConsultationId(expandedConsultationId === consult.id ? null : consult.id)} className="flex items-center gap-4 cursor-pointer group flex-1 min-w-0">
                                    <div className="w-12 h-12 rounded-[7px] bg-violet-50 text-violet-500 flex items-center justify-center font-bold text-sm shadow-inner italic uppercase shrink-0">{consult.type ? consult.type[0] : 'C'}</div>
                                    <div className="min-w-0">
                                        <h4 className="font-black text-slate-800 uppercase tracking-tight text-sm truncate">{consult.type || 'Consulta'}</h4>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest truncate">{formatDate(consult.date)} • {consult.clinic || 'Clínica no registrada'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {!readOnly && onEditRecord && (
                                        <button onClick={() => onEditRecord(consult)} className="w-8 h-8 bg-slate-100 text-slate-500 rounded-[7px] flex items-center justify-center" title="Editar"><Edit2 size={13} /></button>
                                    )}
                                    {!readOnly && onDeleteRecord && (
                                        <button onClick={() => onDeleteRecord(consult)} className="w-8 h-8 bg-rose-50 text-rose-500 rounded-[7px] flex items-center justify-center" title="Eliminar"><Trash2 size={13} /></button>
                                    )}
                                    <button onClick={() => setExpandedConsultationId(expandedConsultationId === consult.id ? null : consult.id)} className="w-8 h-8 flex items-center justify-center">
                                        <ChevronDown size={20} className={`text-slate-300 transition-transform duration-500 ${expandedConsultationId === consult.id ? 'rotate-180 text-violet-500' : 'group-hover:translate-y-1'}`} />
                                    </button>
                                </div>
                            </div>
                            {expandedConsultationId === consult.id && (
                                <div className="p-6 bg-slate-50 border-t border-slate-100 animate-in slide-in-from-top-4 duration-500">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div className="bg-white p-4 rounded-[7px] border border-violet-100 shadow-sm">
                                            <p className="text-[9px] font-black text-violet-400 uppercase tracking-widest mb-2">Diagnóstico</p>
                                            <p className="text-xs font-bold text-slate-700 leading-relaxed italic">"{consult.diagnosis || 'Sin registro'}"</p>
                                        </div>
                                        <div className="bg-white p-4 rounded-[7px] border border-violet-100 shadow-sm">
                                            <p className="text-[9px] font-black text-violet-400 uppercase tracking-widest mb-2">Tratamiento</p>
                                            <p className="text-xs font-bold text-slate-700 leading-relaxed italic">"{consult.recommendation || 'Sin registro'}"</p>
                                        </div>
                                    </div>
                                    {consult.prescriptions && Array.isArray(consult.prescriptions) && consult.prescriptions.length > 0 && (
                                        <div className="bg-white p-4 rounded-[7px] border border-violet-100 shadow-sm">
                                            <p className="text-[9px] font-black text-violet-400 uppercase tracking-widest mb-3">Recetas</p>
                                            <div className="space-y-2">
                                                {consult.prescriptions.map((p: any, idx: number) => (
                                                    <div key={idx} className="flex items-center gap-3 bg-slate-50 p-2 rounded-[7px]">
                                                        <div className="w-8 h-8 rounded-[7px] bg-violet-100 text-violet-500 flex items-center justify-center shrink-0"><Pill size={14} /></div>
                                                        <div>
                                                            <p className="text-xs font-black text-slate-700 uppercase">{p.name}</p>
                                                            <p className="text-[8px] font-bold text-slate-400 uppercase">{p.freq} hrs / {p.duration} días</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        );
    }

    return null;
};
